/**
 * GreenEnergyPreview - Home page green energy section preview
 * Redesigned for a high-end clean technical presentation
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBolt, FaThermometerHalf, FaMicrochip, FaChargingStation, FaSeedling, FaArrowRight } from 'react-icons/fa';
import { StaggerContainer, StaggerItem } from '../ui/AnimatedSection';

const greenServices = [
  { icon: FaBolt, title: 'Ashore Marine Power System', desc: 'High-voltage shore connection (cold ironing) allowing vessels to plug into land grids.' },
  { icon: FaThermometerHalf, title: 'Fuel Electric Heating System', desc: 'Decarbonized electrical heating systems for tankers and bulk liquid terminals.' },
  { icon: FaMicrochip, title: 'VFD & Energy Optimization', desc: 'Variable Frequency Drives and control architectures for heavy port machinery.' },
  { icon: FaChargingStation, title: 'Port Electrification', desc: 'Grid engineering and fast charging infrastructures for terminal tractors and tugboats.' },
  // { icon: FaSeedling, title: 'Sustainable Port Planning', desc: 'Strategic advisory and technical implementations to achieve net zero terminal footprints.' }
];

const GreenEnergyPreview = () => (
  <section style={{
    padding: '4.5rem 0',
    background: 'linear-gradient(135deg, #020F0A 0%, #051D14 100%)',
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid rgba(16, 185, 129, 0.12)'
  }}>
    {/* Subtle architectural vertical lines */}
    <div style={{
      position: 'absolute', top: 0, bottom: 0, left: '20%', width: '1px',
      background: 'rgba(16, 185, 129, 0.03)', pointerEvents: 'none'
    }} />
    <div style={{
      position: 'absolute', top: 0, bottom: 0, left: '80%', width: '1px',
      background: 'rgba(16, 185, 129, 0.03)', pointerEvents: 'none'
    }} />

    <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>

      {/* Header */}
      <div className="text-start mb-5" style={{ maxWidth: '650px' }}>
        <span className="section-tag" style={{ background: 'rgba(16,185,129,0.06)', color: '#10B981', border: '1px solid rgba(16,185,129,0.18)' }}>
          Green Infrastructure
        </span>
        <h2 className="section-title light mt-2" style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 800,
          fontSize: 'clamp(2rem, 4vw, 2.5rem)',
          color: '#FFFFFF',
          letterSpacing: '-0.02em',
          lineHeight: 1.15
        }}>
          Powering Sustainable &amp;<br />Net Zero Port Facilities
        </h2>
        <div className="section-divider" style={{ width: '40px', background: '#10B981', height: '1px', margin: '1.5rem 0' }} />
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.92rem', lineHeight: '1.8', textAlign: 'justify' }}>
          Through our exclusive partnership with Shanghai Hissen Engineering, we introduce advanced green energy solutions to Indian terminals — mitigating emissions, cutting operating costs, and establishing clean sea portals.
        </p>
      </div>

      {/* Cards Grid */}
      <StaggerContainer className="row g-3" staggerDelay={0.06}>
        {greenServices.map((s, i) => {
          const IconComp = s.icon;
          return (
            <StaggerItem key={i} className="col-lg col-md-6 col-sm-12">
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{
                  background: 'rgba(7, 31, 21, 0.65)',
                  border: '1px solid rgba(16, 185, 129, 0.18)',
                  borderRadius: 2,
                  padding: '2rem 1.6rem',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  transition: 'border-color 0.2s ease, background-color 0.2s ease'
                }}
                className="green-preview-card"
              >
                <div style={{
                  width: '34px', height: '34px', borderRadius: 2,
                  background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1.5rem'
                }}>
                  <IconComp size={14} color="#10B981" />
                </div>
                <h5 style={{
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  color: '#fff', fontSize: '0.9rem', marginBottom: '0.6rem',
                  letterSpacing: '-0.01em'
                }}>
                  {s.title}
                </h5>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', lineHeight: '1.65', margin: 0, textAlign: 'justify' }}>
                  {s.desc}
                </p>
              </motion.div>
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      {/* Bottom Banner Row */}
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-5 pt-4"
        style={{ borderTop: '1px solid rgba(16, 185, 129, 0.15)', gap: '1.5rem' }}>
        <div>
          <h3 style={{
            fontFamily: 'var(--font-heading)', fontWeight: 700,
            fontSize: '1.15rem', color: '#fff', margin: 0, letterSpacing: '-0.01em'
          }}>
            Powering Possibilities.{' '}
            <span style={{ color: '#10B981' }}>Delivering Excellence.</span>
          </h3>
        </div>
        
        <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
          <Link to="/services#green-energy" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.65rem',
            background: '#10B981',
            color: 'var(--navy)',
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            padding: '0.9rem 2.2rem',
            borderRadius: 2,
            textDecoration: 'none',
            fontSize: '0.78rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            transition: 'all 0.25s ease'
          }} className="btn-green-cta">
            Explore Green Solutions <FaArrowRight size={9} />
          </Link>
        </motion.div>
      </div>
    </div>

    <style>{`
      .green-preview-card:hover {
        border-color: #10B981 !important;
        background-color: rgba(10, 48, 32, 0.85) !important;
      }
      .btn-green-cta:hover {
        background: #34D399 !important;
        color: var(--navy) !important;
      }
    `}</style>
  </section>
);

export default GreenEnergyPreview;
